// src/contexts/SchoolContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { School } from '../types/admin';
import { useAuth } from './AuthContext';

interface SchoolContextType {
  selectedSchool: School | null;
  setSelectedSchool: (school: School | null) => Promise<void>;
  loading: boolean;
}

const SchoolContext = createContext<SchoolContextType>({
  selectedSchool: null,
  setSelectedSchool: async () => {},
  loading: true,
});

export function useSchool() {
  return useContext(SchoolContext);
}

export function SchoolProvider({ children }: { children: React.ReactNode }) {
  const [selectedSchool, setSelectedSchoolState] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load the saved school (if any) from localStorage on mount
  useEffect(() => {
    const loadSchool = async () => {
      if (!user) {
        console.log('[SchoolContext] No user, skipping school load');
        setLoading(false);
        return;
      }
      
      console.log('[SchoolContext] Loading school for user:', user.uid);
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          console.log('[SchoolContext] User document not found');
          setLoading(false);
          return;
        }
        
        // Attempt to load the full school object from localStorage
        const savedSchoolString = localStorage.getItem('selectedSchool');
        if (savedSchoolString) {
          const savedSchool: School = JSON.parse(savedSchoolString);
          console.log('[SchoolContext] Found saved school in localStorage:', savedSchool);
          
          // Verify membership using both possible membership ID formats
          const membershipId = `${savedSchool.id}_${user.uid}`;
          const altMembershipId = `${user.uid}_${savedSchool.id}`;
          const membershipDoc = await getDoc(doc(db, 'school_members', membershipId));
          const altMembershipDoc = await getDoc(doc(db, 'school_members', altMembershipId));
          
          if (membershipDoc.exists() || altMembershipDoc.exists()) {
            console.log('[SchoolContext] Valid membership found for saved school');
            setSelectedSchoolState(savedSchool);
          } else {
            console.log('[SchoolContext] No valid membership for saved school, removing from storage');
            localStorage.removeItem('selectedSchool');
          }
        } else {
          console.log('[SchoolContext] No saved school in localStorage, attempting auto-discovery');
          
          // Try to auto-discover user's schools
          try {
            const { collection, query, where, getDocs, doc, getDoc } = await import('firebase/firestore');
            const membershipQuery = query(
              collection(db, 'school_members'),
              where('userId', '==', user.uid)
            );
            const membershipSnapshot = await getDocs(membershipQuery);
            
            if (membershipSnapshot.size > 0) {
              console.log('[SchoolContext] Found', membershipSnapshot.size, 'school memberships');
              
              // Get school details for all memberships
              const schools = [];
              for (const memberDoc of membershipSnapshot.docs) {
                const schoolId = memberDoc.data().schoolId;
                const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
                if (schoolDoc.exists()) {
                  schools.push({ id: schoolDoc.id, ...schoolDoc.data() });
                }
              }
              
              // If user is member of exactly one school, auto-select it
              if (schools.length === 1) {
                console.log('[SchoolContext] Auto-selecting single school:', schools[0]);
                const school = schools[0];
                setSelectedSchoolState(school);
                localStorage.setItem('selectedSchool', JSON.stringify(school));
              } else if (schools.length > 1) {
                console.log('[SchoolContext] User is member of multiple schools, will show selector');
              }
            } else {
              console.log('[SchoolContext] No school memberships found for user');
            }
          } catch (autoDiscoveryError) {
            console.error('[SchoolContext] Error during auto-discovery:', autoDiscoveryError);
          }
        }
      } catch (error) {
        console.error('[SchoolContext] Error loading school:', error);
        localStorage.removeItem('selectedSchool');
      } finally {
        setLoading(false);
      }
    };

    loadSchool();
  }, [user]);

  // Updated setter that saves the entire school object to localStorage
  const handleSetSelectedSchool = async (school: School | null) => {
    console.log('[SchoolContext] Setting selected school:', school?.id);
    
    if (school && user) {
      const membershipId = `${school.id}_${user.uid}`;
      const altMembershipId = `${user.uid}_${school.id}`;
      
      try {
        const membershipDoc = await getDoc(doc(db, 'school_members', membershipId));
        const altMembershipDoc = await getDoc(doc(db, 'school_members', altMembershipId));
        
        if (membershipDoc.exists() || altMembershipDoc.exists()) {
          console.log('[SchoolContext] Valid membership found, setting school');
          setSelectedSchoolState(school);
          localStorage.setItem('selectedSchool', JSON.stringify(school));
          console.log('[SchoolContext] School set and saved to localStorage');
        } else {
          console.log('[SchoolContext] No valid membership found for school');
          setSelectedSchoolState(null);
          localStorage.removeItem('selectedSchool');
        }
      } catch (error) {
        console.error('[SchoolContext] Error verifying school membership:', error);
        setSelectedSchoolState(null);
        localStorage.removeItem('selectedSchool');
      }
    } else {
      console.log('[SchoolContext] Clearing selected school');
      setSelectedSchoolState(null);
      localStorage.removeItem('selectedSchool');
    }
  };

  return (
    <SchoolContext.Provider
      value={{
        selectedSchool,
        setSelectedSchool: handleSetSelectedSchool,
        loading,
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
}
